import { useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import {
  InputTable,
  type InputTableCellParams,
  type InputTableRowParams,
  type RepeatRowsPayload
} from '@/components/custom/erp';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import { SelectedCellInfo, useCustomerBalance } from '../../../hooks';
import type { DetailRow } from '../hooks/useDetailRows';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { useDetailPaste } from '../hooks';
import { FormMode } from '@/types/form';
import ActionBar from './ActionBar';

interface DetailTabProps {
  formMode: FormMode;
  rows: DetailRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams<DetailRow>) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<unknown[]>; resultField?: string }>
  ) => void;
  status?: string;
  loaiHD?: string;
  setNhomLoaiHD?: (value: string) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<DetailRow>) => void;
  isPasting?: boolean;
}

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onCellClick,
  onRowClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  status,
  loaiHD,
  setNhomLoaiHD,
  onRepeatForRows,
  isPasting
}) => {
  const [dienGiai, ngayCt] = useWatch({ name: ['dien_giai', 'ngay_ct'] });
  const balanceItems = useMemo(
    () =>
      rows.map(row => ({
        ma_kh: (row.ma_kh_data as { uuid?: string } | null)?.uuid || '',
        tk: (row.tk_no_data as { uuid?: string } | null)?.uuid
      })),
    [rows]
  );
  const { getCustomerBalance } = useCustomerBalance({ ngayCt, items: balanceItems });
  useInvoiceHydration({
    rows,
    enabled: formMode !== 'add',
    onHydrated: (uuid, invoice) => onCellValueChange(uuid, 'id_hd_data', invoice)
  });
  const columns = useMemo(
    () =>
      getDetailTableColumns(onCellValueChange, status, loaiHD, setNhomLoaiHD, getCustomerBalance, dienGiai, formMode),
    [onCellValueChange, status, loaiHD, setNhomLoaiHD, getCustomerBalance, dienGiai, formMode]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          handleViewInventory={() => console.log('View inventory clicked')}
          handleViewReceipt={() => console.log('View receipt clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
